import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './AnalysisGrid.css';
import api from '../api/axiosConfig';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';
import { format, isValid, startOfMonth, subMonths, endOfMonth } from 'date-fns';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Radio, RadioGroup, FormControlLabel } from '@mui/material';

// 숫자 포맷
const formatNumber = (num) => {
  if (num === null || num === undefined || num === '') return '0';
  const number = parseFloat(num.toString().replace(/,/g, ''));
  if (isNaN(number) || number === 0) return '0';
  return number.toLocaleString();
};

// 숫자만 추출 (엑셀용)
const parseNumberForExcel = (num) => {
  if (num === null || num === undefined || num === '') return 0;
  const number = parseFloat(num.toString().replace(/,/g, ''));
  return isNaN(number) ? 0 : number;
};

const Total3 = () => {
    const [rows, setRows] = useState([]);
    const [data, setData] = useState([]);
    const [sort, setSort] = useState('A');

    const today = new Date();
    const [searchDate, setSearchDate] = useState(today);

    const thisYear = searchDate.getFullYear();
    const lastYear = thisYear - 1;
    const twoYearsAgoYear = thisYear - 2;

    const threeMonthsAgo = subMonths(today, 3);
    const [startDate, setStartDate] = useState(startOfMonth(threeMonthsAgo));
    const [endDate, setEndDate] = useState(endOfMonth(today));

    const [fieldId, setFieldId] = useState('');
    const [fieldOptions, setFieldOptions] = useState([]);

    const [rowsWithSubtotal, setRowsWithSubtotal] = useState([]);

    // 합계 상태 분리
    const [totalRow, setTotalRow] = useState({});
    const [taxInvoiceRow, setTaxInvoiceRow] = useState({});

    // 현장 조회
    useEffect(() => {
        api.get(`/api/fieldList`)
            .then((res) => {
                const uniqueOptions = res.data.filter(
                    (item, index, self) =>
                        index === self.findIndex((t) => t.FIELD_ID === item.FIELD_ID)
                );
                setFieldOptions(uniqueOptions);
            })
            .catch((err) => console.error(err));
    }, []);

    // 수금 현황 조회
    const searchList = useCallback(() => {
        const start = format(startDate, 'yyyy-MM-dd');
        const end = format(endDate, 'yyyy-MM-dd');
        const date = format(searchDate, 'yyyy-MM-dd');

        api.get(`/api/total3/searchList?startDate=${start}&endDate=${end}&date=${date}&fieldId=${fieldId || ''}&sort=${sort}`)
            .then((res) => {
                const rawData = res.data || [];
                const processedData = rawData.map(row => {
                    let month = row.ISSUED_MONTH;
                    let day = row.ISSUED_DAY;
                    if (!month && row.ISSUED_DATE && row.ISSUED_DATE.length >= 10) {
                        month = row.ISSUED_DATE.substring(5, 7);
                        day = row.ISSUED_DATE.substring(8, 10);
                    }
                    return {
                        ...row,
                        ISSUED_MONTH: month,
                        ISSUED_DAY: day,
                        ETC_YN: row.ETC_YN || 'N'
                    };
                });
                setData(processedData);
                setRows(processedData);
            })
            .catch((err) => console.error(err));
    }, [startDate, endDate, fieldId, sort, searchDate]);

    useEffect(() => {
        searchList();
    }, [searchList]);

    // 합계 계산 로직
    const calculateSubtotals = (list) => {
        if (!list || list.length === 0) {
            setRowsWithSubtotal([]);
            setTotalRow({});
            return;
        }

        const sum = {
            TOTAL_AMOUNT: 0,
            OUT_TOTAL_AMOUNT: 0,
            TWO_YEAR_AMOUNT: 0,
            LAST_YEAR_AMOUNT: 0,
            THIS_YEAR_AMOUNT: 0,
            PAID_AMOUNT: 0,
            UNPAID_AMOUNT: 0,
            UNPAID_PROVIDE_AMOUNT: 0,
        };

        list.forEach((row) => {
            for (const key in sum) {
                sum[key] += parseFloat(row[key]) || 0;
            }
        });

        setRowsWithSubtotal(list);
        setTotalRow(sum);
    };

    useEffect(() => {
        calculateSubtotals(rows);
    }, [rows]);

    // =========================
    // 📊 엑셀 내려받기
    // =========================

    const exportToExcel = async () => {
    try {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('수금현황');

        // ── 열 너비 설정 (A~J, 10컬럼) ──────────────────────────
        worksheet.columns = [
            { width: 7  }, // A: 순번
            { width: 25 }, // B: 현장명
            { width: 18 }, // C: 계약금액
            { width: 18 }, // D: 청구금액
            { width: 15 }, // E: 2년전
            { width: 15 }, // F: 작년
            { width: 15 }, // G: 올해
            { width: 15 }, // H: 합계
            { width: 15 }, // I: 미수금액
            { width: 15 }, // J: 미청구액
        ];

        let rowNum = 1;

        // ── 1. 제목 ───────────────────────────────────────────────
        worksheet.mergeCells(`A${rowNum}:J${rowNum}`);
        const titleCell = worksheet.getCell(`A${rowNum}`);
        titleCell.value = `${thisYear}년 기성금 청구 수금 현황`;
        titleCell.font = { size: 16, bold: true, name: '굴림체' };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getRow(rowNum).height = 40;
        rowNum++;

        // ── 2. 기준일자 ───────────────────────────────────────────
        worksheet.mergeCells(`A${rowNum}:J${rowNum}`);
        const dateCell = worksheet.getCell(`A${rowNum}`);
        dateCell.value = `기준일자: ${format(searchDate, 'yyyy.MM.dd')}`;

        dateCell.font = { size: 10, name: '굴림체' };
        dateCell.alignment = { horizontal: 'right', vertical: 'middle' };
        worksheet.getRow(rowNum).height = 20;
        rowNum++;

        // ── 헬퍼: 셀 공통 스타일 ─────────────────────────────────
        const applyStyle = (cell, opts = {}) => {
            const {
                bold = false, color = null, fill = null,
                align = 'center', numFmt = null, size = 10
            } = opts;
            cell.font = { bold, name: '굴림체', size, ...(color ? { color: { argb: color } } : {}) };
            cell.alignment = { horizontal: align, vertical: 'middle' };
            cell.border = {
                top: { style: 'thin' }, left: { style: 'thin' },
                bottom: { style: 'thin' }, right: { style: 'thin' }
            };
            if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
            if (numFmt) cell.numFmt = numFmt;
        };

        // ── 3. 헤더 1행 ───────────────────────────────────────────
        const headerFill = 'FFCCE5FF'; // 연파랑
        const hRow1 = rowNum;

        // rowspan 2 셀들
        const span2Cols = [
            { col: 1, label: '순번' },
            { col: 2, label: '현장명' },
            { col: 3, label: '계약금액' },
            { col: 4, label: '청구금액' },
            { col: 9, label: '미수금액' },
            { col: 10, label: '미청구액' },
        ];
        span2Cols.forEach(({ col, label }) => {
            worksheet.mergeCells(rowNum, col, rowNum + 1, col);
            const cell = worksheet.getCell(rowNum, col);
            cell.value = label;
            applyStyle(cell, { bold: true, fill: headerFill });
        });

        // 입금액 colspan 4
        worksheet.mergeCells(rowNum, 5, rowNum, 8);
        const paidHeader = worksheet.getCell(rowNum, 5);
        paidHeader.value = '입금액';
        applyStyle(paidHeader, { bold: true, fill: headerFill });

        worksheet.getRow(rowNum).height = 22;
        rowNum++;

        // ── 4. 헤더 2행 (입금액 세부) ─────────────────────────────
        const subHeaders = [
            { col: 5, label: `${twoYearsAgoYear}년` },
            { col: 6, label: `${lastYear}년` },
            { col: 7, label: `${thisYear}년` },
            { col: 8, label: '합계' },
        ];
        subHeaders.forEach(({ col, label }) => {
            const cell = worksheet.getCell(rowNum, col);
            cell.value = label;
            applyStyle(cell, { bold: true, fill: headerFill });
        });
        worksheet.getRow(rowNum).height = 22;
        rowNum++;

        // ── 5. 데이터 행 ─────────────────────────────────────────
        const MIN_ROWS = 20;
        const totalDataRows = Math.max(rowsWithSubtotal.length, MIN_ROWS);
        const numCols = [3, 4, 5, 6, 7, 8, 9, 10]; // 숫자 포맷 컬럼

        for (let i = 0; i < totalDataRows; i++) {
            const row = rowsWithSubtotal[i];
            const exRow = worksheet.getRow(rowNum);

            exRow.getCell(1).value = row ? i + 1 : '';
            exRow.getCell(2).value = row?.FIELD_NAME || '';
            exRow.getCell(3).value = row ? parseNumberForExcel(row.TOTAL_AMOUNT)           : '';
            exRow.getCell(4).value = row ? parseNumberForExcel(row.OUT_TOTAL_AMOUNT)       : '';
            exRow.getCell(5).value = row ? parseNumberForExcel(row.TWO_YEAR_AMOUNT)        : '';
            exRow.getCell(6).value = row ? parseNumberForExcel(row.LAST_YEAR_AMOUNT)       : '';
            exRow.getCell(7).value = row ? parseNumberForExcel(row.THIS_YEAR_AMOUNT)       : '';
            exRow.getCell(8).value = row ? parseNumberForExcel(row.PAID_AMOUNT)            : '';
            exRow.getCell(9).value = row ? parseNumberForExcel(row.UNPAID_AMOUNT)          : '';
            exRow.getCell(10).value = row ? parseNumberForExcel(row.UNPAID_PROVIDE_AMOUNT) : '';

            for (let c = 1; c <= 10; c++) {
                const cell = exRow.getCell(c);
                const isNum = numCols.includes(c);
                const isUnpaid = c === 9; // 미수금 파란색
                applyStyle(cell, {
                    align: c === 2 ? 'left' : (isNum ? 'right' : 'center'),
                    numFmt: isNum ? '#,##0' : null,
                    color: isUnpaid && row ? 'FF0048FF' : null,
                });
            }
            exRow.height = 20;
            rowNum++;
        }

        // ── 6. 합계 행 ────────────────────────────────────────────
        const sumFill = 'FFA8E6A8';
        const sumRow = worksheet.getRow(rowNum);
        worksheet.mergeCells(rowNum, 1, rowNum, 2);
        sumRow.getCell(1).value = '합 계';
        sumRow.getCell(3).value = parseNumberForExcel(totalRow.TOTAL_AMOUNT);
        sumRow.getCell(4).value = parseNumberForExcel(totalRow.OUT_TOTAL_AMOUNT);
        sumRow.getCell(5).value = parseNumberForExcel(totalRow.TWO_YEAR_AMOUNT);
        sumRow.getCell(6).value = parseNumberForExcel(totalRow.LAST_YEAR_AMOUNT);
        sumRow.getCell(7).value = parseNumberForExcel(totalRow.THIS_YEAR_AMOUNT);
        sumRow.getCell(8).value = parseNumberForExcel(totalRow.PAID_AMOUNT);
        sumRow.getCell(9).value = parseNumberForExcel(totalRow.UNPAID_AMOUNT);
        sumRow.getCell(10).value = parseNumberForExcel(totalRow.UNPAID_PROVIDE_AMOUNT);

        for (let c = 1; c <= 10; c++) {
            const cell = sumRow.getCell(c);
            const isNum = numCols.includes(c);
            const isUnpaid = c === 9;
            applyStyle(cell, {
                bold: true, fill: sumFill,
                align: c <= 2 ? 'center' : (isNum ? 'right' : 'center'),
                numFmt: isNum ? '#,##0' : null,
                color: isUnpaid ? 'FF0048FF' : null,
            });
        }
        sumRow.height = 22;

        // ── 7. 파일 저장 ──────────────────────────────────────────
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(
            new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }),
            `수금현황_${format(new Date(), 'yyyyMMdd')}.xlsx`
        );

    } catch (error) {
        console.error('엑셀 내보내기 오류:', error);
        alert('엑셀 내보내기 중 오류가 발생했습니다.');
    }
};

    // 인쇄
    const handlePrint = () => {
        const printContents = document.getElementById('print-area').innerHTML;
        const originalContents = document.body.innerHTML;
        document.body.innerHTML = printContents;
        window.print();
        document.body.innerHTML = originalContents;
        window.location.reload();
    };

    // =========================
    // 렌더링
    // =========================
    return (
        <div className="grid-container">
            <h2>수금현황</h2>

            <div className="gh-band">
                <div className="gh-band__row">
                    <div className="gh-band__title">
                        <span className="gh-octo" aria-hidden>▶</span>
                        <span>조회조건</span>
                    </div>
                </div>
                <div className="gh-band__filters">

                    <div className="filter-item gh-field">
                        <label>현장</label>
                        <Autocomplete
                            options={fieldOptions}
                            value={fieldOptions.find((option) => option.FIELD_ID === fieldId) || null}
                            onChange={(event, newValue) => {
                                setFieldId(newValue ? newValue.FIELD_ID : '');
                            }}
                            getOptionLabel={(option) => option?.FIELD_NAME || ''}
                            isOptionEqualToValue={(option, value) => option.FIELD_ID === value.FIELD_ID}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="standard"
                                    placeholder="현장 선택"
                                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                                    sx={{ px: 1, minWidth: 200 }}
                                />
                            )}
                        />
                    </div>
                    <div className="filter-item gh-field">
                    <label>기준일</label>
                        <DatePicker
                          id="dayPicker"
                          selected={searchDate}
                          onChange={(date) => setSearchDate(date)}
                          dateFormat="yyyy-MM-dd"          // 일 선택
                          placeholderText="날짜 선택"
                          locale={ko}
                          className="gh-input"
                          portalId="portal-root"
                          popperPlacement="bottom-start"
                        />
                    </div>

                      <div className="filter-item gh-field">
                        <label>정렬</label>
                        <RadioGroup
                          row
                          value={sort}
                          onChange={(e) => setSort(e.target.value)}
                          sx={{ px: 1 }}
                        >
                          <FormControlLabel value="A" control={<Radio size="small" />} label="현장명"
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px' } }} />
                          <FormControlLabel value="B" control={<Radio size="small" />} label="미수금액"
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px' } }} />
                          <FormControlLabel value="C" control={<Radio size="small" />} label="미청구액"
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px' } }} />
                        </RadioGroup>
                      </div>

                    <div className="filter-actions gh-actions">
                        <button className="btn" onClick={searchList}>조회</button>
                    </div>
                </div>
            </div>

            <div className="button-group-right">
                <button onClick={exportToExcel} className="btn btn-primary">엑셀 내려받기</button>
                <button onClick={handlePrint} className="btn btn-ghost">출력하기</button>
            </div>

            <div id="print-area">
                <div style={{ border: '1px solid black', textAlign: 'center' }}>
                    <span style={{ fontSize: '20px', fontWeight: 'bold'}}>{format(searchDate, 'yyyy')}년 기성금 청구 수금 현황</span>
                </div>
                <div style={{ marginBottom: '10px', fontWeight: 'bold', textAlign: 'right' }}>
                      기준일자: {format(searchDate, 'yyyy.MM.dd')}
                </div>

                <div className="grid-table-wrapper">
                    <table className="grid-table">
                        <colgroup>
                            <col style={{ width: '5%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                            <col style={{ width: '10%' }} />
                        </colgroup>
                        <thead>
                            <tr>
                                <th rowSpan="2" ><strong>순번</strong></th>
                                <th rowSpan="2" ><strong>현장명</strong></th>
                                <th rowSpan="2" ><strong>계약금액</strong></th>
                                <th rowSpan="2" ><strong>청구금액</strong></th>
                                <th colSpan="4"><strong>입금액</strong></th>
                                <th rowSpan="2">미수금액</th>
                                <th rowSpan="2">미청구액</th>
                            </tr>
                            <tr>
                                <th ><span>{twoYearsAgoYear}년 </span></th>
                                <th ><span>{lastYear}년 </span></th>
                                <th ><span>{format(searchDate, 'yyyy')}년 </span></th>
                                <th ><span>합계 </span></th>
                            </tr>
                        </thead>
                        <tbody>

                            {(() => {
                                const result = [];
                                let i = 0;
                                let dataRowCount = 0;
                                let seq = 1; // ⭐ 순번 카운터 추가

                                while (i < rowsWithSubtotal.length) {
                                    const currentRow = rowsWithSubtotal[i];
                                    const currentMonth = currentRow.ISSUED_MONTH;

                                    let spanCount = 0;
                                    for (let j = i; j < rowsWithSubtotal.length; j++) {
                                        if (rowsWithSubtotal[j].ISSUED_MONTH === currentMonth) spanCount++;
                                        else break;
                                    }

                                    for (let k = 0; k < spanCount; k++) {
                                        const row = rowsWithSubtotal[i + k];
                                        const isSubtotal = row.isSubtotal;
                                        dataRowCount++;

                                        result.push(
                                            <tr key={i + k} className={row.isEtctotal ? 'etctotal-row' : (isSubtotal ? 'subtotal-row' : '')}>
                                                <td style={{ textAlign: 'center' }}>{seq++}</td>
                                                <td style={{ textAlign: 'left' }}>{row.FIELD_NAME}</td>
                                                <td><span className="right-align">{formatNumber(row.TOTAL_AMOUNT)}</span></td>
                                                <td><span className="right-align">{formatNumber(row.OUT_TOTAL_AMOUNT)}</span></td>
                                                <td><span className="right-align">{formatNumber(row.TWO_YEAR_AMOUNT)}</span></td>
                                                <td><span className="right-align">{formatNumber(row.LAST_YEAR_AMOUNT)}</span></td>
                                                <td><span className="right-align">{formatNumber(row.THIS_YEAR_AMOUNT)}</span></td>
                                                <td><span className="right-align">{formatNumber(row.PAID_AMOUNT)}</span></td>
                                                <td><strong><span style={{ color: '#0048ff' }} className="right-align">{formatNumber(row.UNPAID_AMOUNT)}</span></strong></td>
                                                <td><span className="right-align">{formatNumber(row.UNPAID_PROVIDE_AMOUNT)}</span></td>
                                            </tr>
                                        );
                                    }
                                    i += spanCount;
                                }

                                // 빈 행 채우기
                                const MIN_ROWS = 20;
                                const emptyRowCount = Math.max(0, MIN_ROWS - dataRowCount);
                                for (let e = 0; e < emptyRowCount; e++) {
                                    result.push(
                                        <tr key={`empty-${e}`}>
                                            <td>&nbsp;</td>
                                            <td>&nbsp;</td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                        </tr>
                                    );
                                }

                                return result;
                            })()}

                            {/* 3. 전체 합계 */}

                                <tr style={{ background: '#a8e6a8', fontWeight: 'bold' }}>
                                    <td colSpan="2" style={{ textAlign: 'center' }}>합 계</td>
                                    <td><span className="right-align">{formatNumber(totalRow.TOTAL_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalRow.OUT_TOTAL_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalRow.TWO_YEAR_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalRow.LAST_YEAR_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalRow.THIS_YEAR_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalRow.PAID_AMOUNT)}</span></td>
                                    <td><span style={{ color: '#0048ff' }} className="right-align">{formatNumber(totalRow.UNPAID_AMOUNT)}</span></td>
                                    <td><span className="right-align">{formatNumber(totalRow.UNPAID_PROVIDE_AMOUNT)}</span></td>
                                </tr>

                        </tbody>
                    </table>

                </div>
            </div>
        </div>
    );
};

export default Total3;